// Layer 1 of the fixture: a plausible ledger with NO NETWORK AT ALL. Built
// straight from the player's own `duration` and a fixed slice width, so it
// works before a single byte of the video has been fetched, and it needs
// nothing from GCS, the API or the chain.
//
// Two halves:
//
//  1. buildSyntheticRoot / buildSyntheticRenditions fabricate a single-lane
//     RootVerified / RenditionVerified[] pair — the exact input shape
//     `buildLanes` (ledger/lanes.ts) already consumes, so the real ledger
//     wiring (useVerificationLedger) is unchanged by fixture mode. Every
//     field `buildLanes`/`composite` do not read is a harmless placeholder;
//     see verifyRoot.ts / verifyRendition.ts for the full shapes.
//
//  2. createSyntheticLedgerSource is a clock-driven emitter: given the
//     current playhead, it reports the cell under it plus a lookahead,
//     skipping cells already reported. It is a pure state machine over an
//     injected `currentTime` — nothing here reads `Date.now` or sets a timer
//     — so the same sequence of calls always produces the same sequence of
//     results (Acceptance: "fully deterministic and reproducible").
//
// Honours `failSlice`: reaching that index emits exactly one mismatch and the
// source is halted for good — no further result is ever emitted, matching
// "bytes arrived and hashed wrong" being a one-way trip for the real ledger
// too (LaneSet.mismatch is sticky).
import { fakeHashHex } from '../useSimulatedVerification.js'
import type { SegmentVerificationResult } from '../SegmentVerifier.js'
import type { RenditionManifest, RenditionVerified, SegmentEntry } from '../verifyRendition.js'
import type { RootManifest, RootVerified } from '../verifyRoot.js'

/** Fixed slice width for the fixture ledger, per the plan's Scope line. */
export const SYNTHETIC_SLICE_SECONDS = 4

/** "the cell under the playhead plus a configurable lookahead (default 3
 * cells)" — four cells verified per call by default: the one under the
 * playhead and three ahead of it. */
export const DEFAULT_LOOKAHEAD = 3

/**
 * The fixture's one lane.
 *
 * IT IS SHOWN TO THE VIEWER, which it was not when this was written: the modal
 * now prints the lane a slice was checked against, and the level being streamed
 * now. A lane literally called "fixture" made a scripted demo look broken, so
 * it carries an ordinary rendition name instead. Nothing about it is real —
 * every hash in fixture mode is fabricated and the console says so once per
 * page load (see fixtureMode.ts) — this is only about the demo reading as the
 * product rather than as a test harness.
 */
export const SYNTHETIC_RENDITION_NAME = '1080p'

/** What a rendition of that name would be, so the panel's byte and pixel
 * counts have something to count in a demo. Fabricated, as the name is. */
const SYNTHETIC_RESOLUTION = '1920x1080'
const SYNTHETIC_BANDWIDTH = 8_000_000

/** How many `SYNTHETIC_SLICE_SECONDS` cells cover `duration`. Zero (never
 * negative) for a duration that is not yet known — the caller is expected to
 * treat that as "nothing to build yet", matching every other verification
 * surface's handling of an unloaded player. */
export function syntheticSegmentCount(duration: number, slice: number = SYNTHETIC_SLICE_SECONDS): number {
  if (!Number.isFinite(duration) || duration <= 0 || !Number.isFinite(slice) || slice <= 0) return 0
  return Math.max(1, Math.ceil(duration / slice))
}

/** Per-cell durations: `slice` for every cell but (sometimes) the last, which
 * takes whatever is left of `duration` — the same "short final slice" shape a
 * real ladder has. */
function syntheticDurations(duration: number, slice: number): number[] {
  const count = syntheticSegmentCount(duration, slice)
  const durations: number[] = []
  for (let i = 0; i < count; i++) {
    const start = i * slice
    const remaining = duration - start
    durations.push(remaining > 0 && remaining < slice ? remaining : slice)
  }
  return durations
}

/** A single-rendition, fabricated RootVerified sized to `duration`. Every
 * field `buildLanes` does not read (schema, published_at, source, content,
 * thumbnail, subtitles) is a stable placeholder — never random, so a second
 * call with the same arguments is byte-for-byte identical. */
export function buildSyntheticRoot(videoId: string, duration: number, slice: number = SYNTHETIC_SLICE_SECONDS): RootVerified {
  const manifest: RootManifest = {
    schema: 'forum/verification-root/fixture',
    hash_algo: 'sha256',
    video_id: videoId,
    published_at: '',
    revision: 1,
    master_playlist_path: '',
    source: { provider: 'fixture', observed_at: '' },
    content: {
      title: '',
      description: '',
      language: '',
      rotation_adjustment: 0,
      subtitle_offset: 0,
      participants: [],
      speaker_attributions: [],
    },
    thumbnail_path: '',
    thumbnail_hash: '',
    subtitles: [],
    renditions: [
      {
        name: SYNTHETIC_RENDITION_NAME,
        resolution: SYNTHETIC_RESOLUTION,
        codecs: '',
        bandwidth: SYNTHETIC_BANDWIDTH,
        manifest_path: '',
        manifest_hash: '',
        segment_count: syntheticSegmentCount(duration, slice),
      },
    ],
  }
  return { ok: true, manifest, hash: fakeHashHex(`fixture-root:${videoId}`) }
}

/** The matching single-rendition RenditionVerified[]. Segment hashes reuse
 * `fakeHashHex` keyed on `videoId` and index, so they are the SAME hashes a
 * `createSyntheticLedgerSource` built with the same `videoId` reports —
 * stable across a page reload, per the plan's Acceptance criteria. */
export function buildSyntheticRenditions(
  videoId: string,
  duration: number,
  slice: number = SYNTHETIC_SLICE_SECONDS,
): RenditionVerified[] {
  const durations = syntheticDurations(duration, slice)
  const segments: SegmentEntry[] = durations.map((d, index) => ({
    path: `fixture/${index}.ts`,
    hash: syntheticSegmentHash(videoId, index),
    size: 0,
    duration: d,
  }))
  const manifest: RenditionManifest = {
    schema: 'forum/verification-rendition/fixture',
    hash_algo: 'sha256',
    name: SYNTHETIC_RENDITION_NAME,
    resolution: SYNTHETIC_RESOLUTION,
    codecs: '',
    bandwidth: SYNTHETIC_BANDWIDTH,
    average_bandwidth: SYNTHETIC_BANDWIDTH,
    playlist_path: '',
    segment_count: segments.length,
    total_duration: duration,
    segments,
  }
  return [{ ok: true, manifest, hash: fakeHashHex(`fixture-rendition:${videoId}`) }]
}

/** The single hash seed shared by `buildSyntheticRenditions` and
 * `createSyntheticLedgerSource`, so the manifest's recorded hash and the
 * emitted verification result agree — both derived from `videoId` and
 * `index` alone, nothing that changes between a page load and the next. */
function syntheticSegmentHash(videoId: string, index: number): string {
  return fakeHashHex(`fixture-segment:${videoId}:${index}`)
}

/** How many bytes the fixture says a slice was. Nothing was hashed, so this
 * is invented like everything else here: the fabricated bandwidth over the
 * slice's length, moved up to 20% either way by the slice's own fake hash so
 * that no two are the same size — as no two real segments are — and so the
 * same slice is the same size on every page load. */
function syntheticSegmentBytes(videoId: string, index: number, seconds: number): number {
  const wobble = parseInt(syntheticSegmentHash(videoId, index).slice(0, 4), 16) / 0xffff
  return Math.round((SYNTHETIC_BANDWIDTH / 8) * seconds * (0.8 + 0.4 * wobble))
}

export interface SyntheticLedgerSourceOptions {
  videoId: string
  duration: number
  slice?: number
  /** Cells verified per `advance()` call beyond the one under the playhead.
   * Default 3 (four cells total). */
  lookahead?: number
  /** Index that mismatches, or null to never fail. */
  failSlice?: number | null
}

export interface SyntheticLedgerSource {
  /**
   * Advances the fixture to `currentTime`, returning any NEW results to
   * report (empty most calls — most cells nearby are already verified).
   * Deterministic: the same sequence of `currentTime` values always produces
   * the same sequence of results, and results are never re-emitted for a
   * cell once reported. After a mismatch fires, every subsequent call
   * returns an empty array — the fixture is halted for good, matching a real
   * mismatch's sticky `LaneSet.mismatch`.
   */
  advance: (currentTime: number) => SegmentVerificationResult[]
}

/**
 * The clock-driven emitter. Verifies the cell under the playhead plus
 * `lookahead` cells ahead of it, skipping anything already verified —
 * seeking backwards into already-verified territory therefore emits nothing,
 * and seeking forwards past unverified cells leaves them `unchecked` (no
 * backfill): only the new playhead's window is ever visited.
 */
export function createSyntheticLedgerSource(opts: SyntheticLedgerSourceOptions): SyntheticLedgerSource {
  const slice = opts.slice ?? SYNTHETIC_SLICE_SECONDS
  const lookahead = opts.lookahead ?? DEFAULT_LOOKAHEAD
  const failSlice = opts.failSlice ?? null
  const count = syntheticSegmentCount(opts.duration, slice)
  const done = new Set<number>()
  let halted = false

  return {
    advance(currentTime: number): SegmentVerificationResult[] {
      if (halted || count === 0) return []

      const clamped = Math.max(0, Math.min(currentTime, opts.duration))
      const playheadIndex = Math.min(count - 1, Math.floor(clamped / slice))
      const lastIndex = Math.min(count - 1, playheadIndex + lookahead)

      const results: SegmentVerificationResult[] = []
      for (let index = playheadIndex; index <= lastIndex; index++) {
        if (done.has(index)) continue
        done.add(index)

        if (index === failSlice) {
          halted = true
          results.push({
            ok: false,
            stage: 'segment',
            kind: 'mismatch',
            detail: `Fixture-scripted mismatch at slice ${index}`,
            rendition: SYNTHETIC_RENDITION_NAME,
            segmentIndex: index,
            expectedHash: syntheticSegmentHash(opts.videoId, index),
            actualHash: fakeHashHex(`fixture-segment-tampered:${opts.videoId}:${index}`),
          })
          break
        }

        results.push({
          ok: true,
          stage: 'segment',
          rendition: SYNTHETIC_RENDITION_NAME,
          segmentIndex: index,
          hash: syntheticSegmentHash(opts.videoId, index),
          bytes: syntheticSegmentBytes(opts.videoId, index, Math.min(slice, opts.duration - index * slice)),
        })
      }
      return results
    },
  }
}
